import { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RecipientTableRow from './RecipientTableRow';

const RecipientTable = ({
  recipients,
  searchQuery,
  onSearchChange,
  editingIndex,
  editValue,
  editError,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onEditChange,
  notificationTypes,
  isSaving,
}) => {
  const filteredRecipients = useMemo(
    () =>
      recipients.filter((recipient) =>
        recipient.recipientEmail.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [recipients, searchQuery]
  );

  return (
    <>
      <Box sx={{ pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          label={`${recipients.length} recipient${recipients.length !== 1 ? 's' : ''}`}
          color="primary"
          variant="outlined"
          size="small"
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search recipients..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange('')} edge="end">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'scroll' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Email Address</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Notification Types</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', width: '150px' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRecipients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery
                      ? `No recipients found matching "${searchQuery}"`
                      : 'No recipients added yet. Add your first recipient above.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRecipients.map((recipient, index) => {
                const originalIndex = recipients.indexOf(recipient);
                return (
                  <RecipientTableRow
                    key={originalIndex}
                    recipient={recipient}
                    isEditing={editingIndex === originalIndex}
                    editValue={editValue}
                    editError={editError}
                    onStartEdit={() => onStartEdit(originalIndex)}
                    onSaveEdit={onSaveEdit}
                    onCancelEdit={onCancelEdit}
                    onDelete={() => onDelete(recipient.id)}
                    onEditChange={onEditChange}
                    notificationTypes={notificationTypes}
                    isSaving={isSaving}
                  />
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default RecipientTable;
